import { TenantSubDomainExtractorService } from '@anecdotes/auth-core';
import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, Observable, switchMap } from 'rxjs';
import { RedirectionAction, RedirectionFailedAction, RegisterHandlerAction } from '../store';
import { destinationProp, RedirectionData, redirectPath } from '../types';

@Injectable()
export class RedirectionService {
  constructor(
    private tenantSubDomainExtractorService: TenantSubDomainExtractorService,
    private actions$: Actions,
    private store: Store
  ) {}

  /**
   * Builds redirection link
   * @param destination the destination page/resource that will handle the redirection
   * @param redirectionData custom params needed to handle the redirection properly
   * @returns redirection link by the following template: https://{{application_origin}}/redirection?destination={{destination}}&param1=value1&param2=value2
   */
  buildRedirectionLink(destination: string, redirectionData: RedirectionData): string {
    const allQueryParams = { [destinationProp]: destination, ...redirectionData };
    const queryParams = Object.entries(allQueryParams)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');

    return `${this.tenantSubDomainExtractorService.getCurrentTenantUrl()}/${redirectPath}?${queryParams}`;
  }

  /**
   * Handles redirection
   * @param destination the destination needed to be handled
   * @param redirectionHandler the callback that will be called for specified destination when redirection is initiated.
   * If the redirectionHandler returns false or an error is thrown in it, the redirection is treated as failed.
   * @returns observable that notifies subscribers about redirection to specific destination
   */
  handleRedirection(
    destination: string,
    redirectionHandler: (redirectionData: RedirectionData) => Promise<boolean | void>
  ): Observable<void> {
    destination = destination.toLocaleLowerCase();
    this.store.dispatch(RegisterHandlerAction({ destination }));

    return this.actions$.pipe(
      ofType(RedirectionAction),
      filter((action) => action.destination === destination),
      switchMap(async (action) => {
        let isRedirectionSuccess: boolean;

        try {
          const handlerReturnValue: any = await redirectionHandler(action.redirectionData);
          isRedirectionSuccess = handlerReturnValue === undefined || handlerReturnValue;
        } catch (error) {
          isRedirectionSuccess = false;
        }

        if (!isRedirectionSuccess) {
          this.store.dispatch(
            RedirectionFailedAction({ destination: action.destination, redirectionData: action.redirectionData })
          );
        }
      })
    );
  }
}
